/**
 * Before/After Comparison Slider Component
 * Handles slide navigation and image comparison functionality
 */

class BAComparisonSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.slides = [];
    this.init();
  }

  init() {
    this.slider = this.querySelector('.bacomp__container');
    this.slides = Array.from(this.querySelectorAll('.bacomp__slide'));
    this.prevBtns = Array.from(this.querySelectorAll('.bacomp__nav-btn--prev'));
    this.nextBtns = Array.from(this.querySelectorAll('.bacomp__nav-btn--next'));
    
    if (this.slides.length > 0) {
      this.setupNavigation();
      this.initImageComparisons();
      this.updateNavigationState();
    }
  }

  setupNavigation() {
    this.prevBtns.forEach(btn => {
      btn.addEventListener('click', () => this.goToPrevious());
    });
    
    this.nextBtns.forEach(btn => {
      btn.addEventListener('click', () => this.goToNext());
    });
    
    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.goToPrevious();
      if (e.key === 'ArrowRight') this.goToNext();
    });
  }

  goToNext() {
    if (this.currentIndex < this.slides.length - 1) {
      this.goToSlide(this.currentIndex + 1);
    }
  }

  goToPrevious() {
    if (this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;
    
    // Remove active class from current slide
    this.slides[this.currentIndex].classList.remove('is-active');
    
    // Update index
    this.currentIndex = index;
    
    // Add active class to new slide
    this.slides[this.currentIndex].classList.add('is-active');
    
    // Update navigation state
    this.updateNavigationState();
    
    // Reinitialize image comparison for the new slide
    const newSlide = this.slides[this.currentIndex];
    const imageComparison = newSlide.querySelector('.bacomp__cmp-slide');
    if (imageComparison && !imageComparison.initialized) {
      this.initImageComparison(imageComparison);
    }
  }

  updateNavigationState() {
    this.prevBtns.forEach(btn => {
      btn.disabled = this.currentIndex === 0;
    });
    
    this.nextBtns.forEach(btn => {
      btn.disabled = this.currentIndex === this.slides.length - 1;
    });
  }

  initImageComparisons() {
    const imageComparisons = this.querySelectorAll('.bacomp__cmp-slide');
    imageComparisons.forEach(comparison => {
      // Initialize the first slide's comparison immediately
      if (comparison.closest('.bacomp__slide').classList.contains('is-active')) {
        this.initImageComparison(comparison);
      }
    });
  }

  initImageComparison(element) {
    if (element.initialized) return;
    
    const container = element.querySelector('.bacomp__cmp-container');
    const button = element.querySelector('.bacomp__cmp-btn');
    
    if (!container || !button) return;
    
    let isDragging = false;
    
    const updatePosition = (clientX) => {
      const rect = container.getBoundingClientRect();
      const x = clientX - rect.left;
      const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
      container.style.setProperty('--percent', `${percent}%`);
    };
    
    const startDrag = (e) => {
      isDragging = true;
      e.preventDefault();
      updatePosition(e.type.includes('touch') ? e.touches[0].clientX : e.clientX);
    };
    
    const drag = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      updatePosition(e.type.includes('touch') ? e.touches[0].clientX : e.clientX);
    };
    
    const stopDrag = () => {
      isDragging = false;
    };
    
    // Mouse events
    button.addEventListener('mousedown', startDrag);
    document.addEventListener('mousemove', drag);
    document.addEventListener('mouseup', stopDrag);
    
    // Touch events
    button.addEventListener('touchstart', startDrag, { passive: false });
    document.addEventListener('touchmove', drag, { passive: false });
    document.addEventListener('touchend', stopDrag);
    
    // Click/tap on container to move slider
    container.addEventListener('click', (e) => {
      if (e.target !== button && !button.contains(e.target)) {
        updatePosition(e.clientX);
      }
    });
    
    element.initialized = true;
  }
}

// Define custom element
if (!customElements.get('ba-comparison-slider')) {
  customElements.define('ba-comparison-slider', BAComparisonSlider);
}
